import { useEffect, useMemo, useState } from 'react';
import {
  fetchPublicReviews,
  importPublicReviewsText,
  markPublicReviewReplied,
  updatePublicReviewRecovery,
} from '../api';
import type { PublicReview, PublicReviewAnalysis, PublicReviewSummary } from '../types';

function parseList(value: string | null | undefined): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map((item) => String(item)) : [];
  } catch {
    return [];
  }
}

function statusClass(value: string) {
  if (['replied', 'sent', 'resolved', 'positive'].includes(value)) return 'bg-emerald-100 text-emerald-700';
  if (['negative', 'needs_review', 'unreplied', 'failed'].includes(value)) return 'bg-red-100 text-red-700';
  if (['drafted', 'in_progress', 'pending'].includes(value)) return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-600';
}

function SummaryCard({ label, value, detail }: { label: string; value: number | string; detail: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
      <div className="mt-1 text-[11px] text-slate-500">{detail}</div>
    </div>
  );
}

function ReviewRow({ review, onChanged }: { review: PublicReview; onChanged: () => Promise<void> }) {
  const [replyText, setReplyText] = useState(review.reply_text || review.draft_reply || '');
  const [note, setNote] = useState(review.service_recovery_note || '');
  const [busy, setBusy] = useState(false);
  const themes = parseList(review.theme_tags);
  const risks = parseList(review.risk_flags);

  async function copyDraft() {
    await navigator.clipboard.writeText(replyText);
  }

  async function markReplied() {
    setBusy(true);
    try {
      await markPublicReviewReplied(review.id, replyText);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function updateRecovery(status: 'not_needed' | 'needs_review' | 'in_progress' | 'resolved') {
    setBusy(true);
    try {
      await updatePublicReviewRecovery(review.id, status, note);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">{review.author_name || 'Google reviewer'}</div>
          <div className="text-[11px] text-slate-500">
            {review.location_name || 'Santaan listing'} · {review.published_label || 'date unknown'}
            {review.rating ? ` · ${review.rating}/5` : ''}
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClass(review.sentiment)}`}>
            {review.sentiment}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClass(review.reply_status)}`}>
            {review.reply_status}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClass(review.private_followup_status)}`}>
            {review.private_followup_status.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
        {review.review_text || 'Rating-only review. Treat as light proof, not a direct patient quote.'}
      </p>

      <div className="mt-2 flex flex-wrap gap-1">
        {themes.map((theme) => (
          <span key={theme} className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
            {theme.replace(/_/g, ' ')}
          </span>
        ))}
        {risks.map((risk) => (
          <span key={risk} className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
            {risk.replace(/_/g, ' ')}
          </span>
        ))}
      </div>

      <label className="mt-3 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        Public Google reply draft
      </label>
      <textarea
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        rows={3}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />

      <label className="mt-3 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        Private recovery note
      </label>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Only for internal service recovery. Do not enter unnecessary patient details."
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />

      <div className="mt-3 flex flex-wrap gap-2">
        <button disabled={busy || !replyText.trim()} onClick={copyDraft} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50">
          Copy reply
        </button>
        <button disabled={busy || !replyText.trim()} onClick={markReplied} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
          Mark Google replied
        </button>
        <button disabled={busy} onClick={() => updateRecovery('in_progress')} className="rounded-lg bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 disabled:opacity-50">
          Start recovery
        </button>
        <button disabled={busy} onClick={() => updateRecovery('resolved')} className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-800 disabled:opacity-50">
          Mark resolved
        </button>
      </div>
    </div>
  );
}

export default function PublicReviewsPanel() {
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [summary, setSummary] = useState<PublicReviewSummary | null>(null);
  const [analysis, setAnalysis] = useState<PublicReviewAnalysis | null>(null);
  const [sourceText, setSourceText] = useState('');
  const [locationName, setLocationName] = useState('Santaan Fertility Clinic And Research Institute');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const topThemes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const review of reviews) {
      for (const theme of parseList(review.theme_tags)) counts.set(theme, (counts.get(theme) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [reviews]);

  async function load() {
    setLoading(true);
    try {
      const data = await fetchPublicReviews();
      setReviews(data.reviews);
      setSummary(data.summary);
      setAnalysis(data.analysis);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function importText() {
    if (!sourceText.trim()) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await importPublicReviewsText(sourceText, locationName);
      setMessage(`Imported ${result.parsed} review row(s).`);
      setSourceText('');
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  async function copyOutput(label: string, text?: string) {
    if (!text?.trim()) return;
    try {
      await navigator.clipboard?.writeText(text);
      setMessage(`${label} copied.`);
    } catch {
      setMessage(`Could not copy ${label.toLowerCase()}. Select the text and copy manually.`);
    }
  }

  const analysisThemes = analysis?.top_themes?.length
    ? analysis.top_themes.map((item) => [item.label, item.count] as const)
    : topThemes.map(([theme, count]) => [theme.replace(/_/g, ' '), count] as const);
  const footprintText = analysis ? [
    `Content URN: ${analysis.content_urn}`,
    `Tracking URL: ${analysis.tracking_url}`,
    `Campaign tag: ${analysis.campaign_tag}`,
    `Hashtags: ${analysis.recommended_hashtags.join(' ')}`,
    `Post footer: ${analysis.post_footer}`,
    'Checklist:',
    ...analysis.publish_checklist.map((item) => `- ${item}`),
  ].join('\n') : '';

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <h3 className="text-sm font-semibold text-slate-900">Google review operations</h3>
        <p className="mt-1 text-xs text-slate-600">CRM handles reply discipline and private service recovery.</p>
        {message && <p className="mt-2 rounded-lg bg-white p-2 text-xs text-slate-700">{message}</p>}
      </section>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <SummaryCard label="Reviews" value={summary?.total ?? 0} detail="In CRM inbox" />
        <SummaryCard label="Need reply" value={summary?.needs_reply ?? 0} detail="Draft or unreplied" />
        <SummaryCard label="Negative" value={summary?.negative ?? 0} detail="Service risk" />
        <SummaryCard label="Recovery" value={summary?.needs_recovery ?? 0} detail="Private follow-up" />
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Import Google review text</h3>
        <p className="mt-1 text-xs text-slate-500">
          Paste the Google Business Profile review export/text while the API connector is being configured.
        </p>
        <input
          value={locationName}
          onChange={(e) => setLocationName(e.target.value)}
          className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          placeholder="Location name"
        />
        <textarea
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          rows={5}
          className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-[11px]"
          placeholder="Paste Google reviews text here..."
        />
        <button disabled={busy || !sourceText.trim()} onClick={importText} className="mt-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
          Import reviews
        </button>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Review themes for marketing</h3>
        <div className="mt-2 flex flex-wrap gap-1">
          {analysisThemes.map(([theme, count]) => (
            <span key={theme} className="rounded-full bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-700">
              {theme} · {count}
            </span>
          ))}
          {!analysisThemes.length && <span className="text-xs text-slate-500">Import reviews to reveal campaign proof bricks.</span>}
        </div>
      </section>

      <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Anonymized content output</h3>
            <p className="mt-1 text-xs text-slate-600">
              Safe drafts based on review themes, not direct patient quotes or identifiable treatment stories.
            </p>
          </div>
          <span className="rounded-full bg-white px-2 py-1 text-[10px] font-semibold text-emerald-700">
            {analysis?.total ?? 0} review signal(s)
          </span>
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-emerald-100 bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Organic post draft</h4>
              <button
                type="button"
                onClick={() => copyOutput('Organic post draft', analysis?.organic_post_draft)}
                disabled={!analysis?.organic_post_draft}
                className="rounded-lg border border-slate-300 px-2 py-1 text-[10px] font-semibold text-slate-700 disabled:opacity-50"
              >
                Copy
              </button>
            </div>
            <textarea
              readOnly
              rows={7}
              value={analysis?.organic_post_draft || 'Import Google reviews to generate a safe organic post draft.'}
              className="mt-2 w-full rounded-lg border border-emerald-100 bg-emerald-50/40 px-3 py-2 text-xs leading-5 text-slate-700"
            />
          </div>

          <div className="rounded-xl border border-emerald-100 bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Meta campaign prompt</h4>
              <button
                type="button"
                onClick={() => copyOutput('Meta campaign prompt', analysis?.meta_copy_prompt)}
                disabled={!analysis?.meta_copy_prompt}
                className="rounded-lg border border-slate-300 px-2 py-1 text-[10px] font-semibold text-slate-700 disabled:opacity-50"
              >
                Copy
              </button>
            </div>
            <div className="mt-2 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-xs font-semibold text-emerald-800">
              Angle: {analysis?.meta_campaign_angle || 'Waiting for review theme'}
            </div>
            <textarea
              readOnly
              rows={6}
              value={analysis?.meta_copy_prompt || 'Import Google reviews to generate a Meta-safe campaign prompt.'}
              className="mt-2 w-full rounded-lg border border-emerald-100 bg-emerald-50/40 px-3 py-2 text-xs leading-5 text-slate-700"
            />
          </div>
        </div>

        <div className="mt-3 rounded-xl border border-amber-100 bg-white p-3">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-700">Safety guardrails</h4>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-600">
            {(analysis?.safety_notes?.length ? analysis.safety_notes : [
              'Do not use reviewer names, direct quotes, photos, or identifiable clinical details without consent.',
              'Do not imply guaranteed pregnancy, IVF success, or that Meta can infer someone has infertility.',
              'Use review themes as proof direction; keep the copy educational, local, and service-focused.',
            ]).map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>

        <div className="mt-3 rounded-xl border border-blue-100 bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-blue-700">Digital footprint kit</h4>
            <button
              type="button"
              onClick={() => copyOutput('Digital footprint kit', footprintText)}
              disabled={!analysis}
              className="rounded-lg border border-slate-300 px-2 py-1 text-[10px] font-semibold text-slate-700 disabled:opacity-50"
            >
              Copy kit
            </button>
          </div>
          <dl className="mt-2 grid gap-2 text-xs md:grid-cols-2">
            <div className="rounded-lg bg-blue-50 p-2">
              <dt className="font-semibold text-blue-700">Content URN</dt>
              <dd className="mt-1 break-all text-slate-700">{analysis?.content_urn || 'Import reviews to generate URN'}</dd>
            </div>
            <div className="rounded-lg bg-blue-50 p-2">
              <dt className="font-semibold text-blue-700">Tracked booking URL</dt>
              <dd className="mt-1 break-all text-slate-700">{analysis?.tracking_url || 'Register content before publishing'}</dd>
            </div>
            <div className="rounded-lg bg-blue-50 p-2">
              <dt className="font-semibold text-blue-700">Campaign tag</dt>
              <dd className="mt-1 break-all text-slate-700">{analysis?.campaign_tag || 'Waiting for theme'}</dd>
            </div>
            <div className="rounded-lg bg-blue-50 p-2">
              <dt className="font-semibold text-blue-700">Hashtags</dt>
              <dd className="mt-1 text-slate-700">{analysis?.recommended_hashtags?.join(' ') || '#SantaanFertility #FertilityCare #Odisha'}</dd>
            </div>
          </dl>
          <div className="mt-2 rounded-lg bg-blue-50 p-2 text-xs text-slate-700">
            <strong className="text-blue-700">Post footer:</strong> {analysis?.post_footer || 'Add the tracked booking URL before publishing.'}
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-600">
            {(analysis?.publish_checklist?.length ? analysis.publish_checklist : [
              'Add this Content URN to the Content Calendar before publishing.',
              'Use the tracked booking URL in the post link, bio link, WhatsApp handoff, or landing CTA.',
              'Add platform URL back into the Content Calendar after publishing.',
            ]).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {loading ? (
        <p className="text-sm text-slate-500">Loading reviews...</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <ReviewRow key={review.id} review={review} onChanged={load} />
          ))}
          {!reviews.length && <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">No public reviews imported yet.</p>}
        </div>
      )}
    </div>
  );
}
